import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Loader2, Save } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = {
  slug: "",
  name: "",
  description: "",
  price: 100,
  duration_min: 60,
  audience: "",
};

export default function ServicesManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // service object or EMPTY

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/services");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const save = async (payload, isNew) => {
    try {
      if (isNew) {
        await api.post("/admin/services", payload);
        toast.success("Service created");
      } else {
        await api.patch(`/admin/services/${editing.id}`, payload);
        toast.success("Service updated");
      }
      setEditing(null);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (svc) => {
    if (!window.confirm(`Delete "${svc.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/services/${svc.id}`);
      toast.success("Service deleted");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="services-manager">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-serif text-2xl text-ink">Services</div>
          <div className="text-sm text-ink-soft">Add, edit or remove the sessions you offer.</div>
        </div>
        <button
          onClick={() => setEditing(EMPTY)}
          data-testid="add-service-button"
          className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm font-medium hover:bg-navy-deep transition-colors"
        >
          <Plus size={14} /> New service
        </button>
      </div>

      {loading && (
        <div className="py-12 flex items-center justify-center text-ink-muted">
          <Loader2 size={18} className="animate-spin mr-2" /> Loading…
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="text-center py-14 text-ink-soft italic font-serif text-lg bg-cream-soft border border-sand/60 rounded-3xl">
          No services yet. Click "New service" to add one.
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((s) => (
            <div key={s.id} className="bg-cream-soft border border-sand/70 rounded-2xl p-5">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-ink-soft mb-1">{s.audience}</div>
                  <div className="font-serif text-xl text-ink">{s.name}</div>
                </div>
                <div className="text-navy font-serif text-2xl">${s.price}</div>
              </div>
              <div className="text-sm text-ink-muted line-clamp-3 mb-3">{s.description}</div>
              <div className="flex items-center justify-between">
                <div className="text-xs text-ink-soft uppercase tracking-[0.18em]">
                  {s.duration_min} min · <span className="lowercase font-mono">{s.slug}</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditing(s)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-cream border border-sand px-3 py-1.5 text-xs hover:border-navy hover:text-navy"
                  >
                    <Pencil size={12} /> Edit
                  </button>
                  <button
                    onClick={() => remove(s)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-cream border border-sand px-3 py-1.5 text-xs hover:border-red-300 hover:text-red-700"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <ServiceForm initial={editing} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}

function ServiceForm({ initial, onSave, onClose }) {
  const isNew = !initial.id;
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(
        {
          slug: form.slug.trim(),
          name: form.name.trim(),
          description: form.description.trim(),
          price: Number(form.price),
          duration_min: Number(form.duration_min),
          audience: form.audience.trim(),
        },
        isNew
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 md:p-10 overflow-y-auto" onClick={onClose}>
      <div
        className="bg-cream rounded-[2rem] p-8 max-w-xl w-full shadow-2xl my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="font-serif text-2xl text-ink">{isNew ? "New service" : "Edit service"}</div>
          <button onClick={onClose} className="text-ink-soft hover:text-ink"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="service-form">
          <Row label="Name">
            <input className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required minLength={2} />
          </Row>
          <Row label="Slug" hint="URL-friendly id (lowercase, dashes). Avoid changing this after bookings exist.">
            <input className={inp} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required pattern="[a-z0-9-]+" />
          </Row>
          <div className="grid grid-cols-2 gap-4">
            <Row label="Price (AUD)">
              <input type="number" min="0" className={inp} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required />
            </Row>
            <Row label="Duration (min)">
              <input type="number" min="15" max="480" className={inp} value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} required />
            </Row>
          </div>
          <Row label="Audience" hint='e.g. "Adults (18+)" or "Ages 12 – 17"'>
            <input className={inp} value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} />
          </Row>
          <Row label="Description">
            <textarea rows={4} className={`${inp} resize-none`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Row>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-full border border-ink/20 px-5 py-2.5 text-sm hover:border-navy hover:text-navy">Cancel</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-6 py-2.5 text-sm font-medium hover:bg-navy-deep disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inp = "w-full bg-cream-soft border border-sand/80 rounded-xl px-4 py-2.5 text-ink focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy";
function Row({ label, hint, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.18em] text-ink-soft mb-1.5 block">{label}</label>
      {children}
      {hint && <div className="text-[11px] text-ink-soft mt-1">{hint}</div>}
    </div>
  );
}
